import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Zap, ChevronDown } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { useArticlePath } from "@/lib/i18n";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { homeTrack } from "../shared";
import { CAT_COLORS, catLabel } from "./LiveFeed";

const WINDOWS = [
  { key: "10m", label: "10 min" },
  { key: "1h", label: "1 hour" },
  { key: "24h", label: "24 hours" },
];

const fmtCount = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n));

const MiniSparkline = ({ vals }: { vals: number[] }) => {
  if (!vals || vals.length < 2 || !vals.some((v) => v > 0)) return null;
  const w = 54, h = 18;
  const max = Math.max(...vals);
  const pts = vals.map((v, i) =>
    `${((i / (vals.length - 1)) * w).toFixed(1)},${(h - 2 - (v / max) * (h - 4)).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden
      className="opacity-75 group-hover:opacity-100 motion-safe:transition-opacity shrink-0">
      <polyline points={pts} fill="none" stroke="#A78BFA" strokeWidth="1.4"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const TrendingRow = ({ t, i, winLabel }: { t: any; i: number; winLabel: string }) => {
  const toArticle = useArticlePath();
  const cat = t.categories?.[0];
  return (
    <Link to={toArticle(t.slug)} onClick={() => homeTrack("trending_now", t.slug)}
      data-testid={`trending-row-${t.slug}`}
      title={`${t.views} article ${t.views === 1 ? "view" : "views"} in the last ${winLabel}`}
      className="group flex items-center gap-3 px-4 py-3 border-t border-white/[0.05] first:border-0 hover:bg-white/[0.03] motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/60">
      <span aria-hidden
        className="flex h-6 w-6 items-center justify-center rounded-full border border-white/[0.14] bg-[#0d0f16] font-mono text-[10.5px] text-gray-200 shrink-0 group-hover:border-[#7C3AED]/50 motion-safe:transition-colors">
        {i + 1}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-news text-[13px] font-semibold text-gray-200 group-hover:text-white leading-snug line-clamp-2 motion-safe:transition-colors">
          {t.title}
        </span>
        <span className="block font-mono text-[8.5px] uppercase tracking-[0.14em] text-gray-500 mt-1">
          <span style={{ color: CAT_COLORS[cat || ""] || "#8b93a7" }}>{catLabel(cat)}</span>
          <span className="text-gray-700"> · </span>{timeAgo(t.published_at)}
        </span>
      </span>
      <span className="flex flex-col items-end gap-1 shrink-0">
        <MiniSparkline vals={t.series} />
        <span className="font-mono text-[11px] font-bold text-gray-200" data-testid={`trending-metric-${t.slug}`}>
          {fmtCount(t.views)}
        </span>
      </span>
    </Link>
  );
};

export const TrendingPanel = () => {
  const [win, setWin] = useState("10m");
  const touched = useRef(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["trending-velocity", win],
    queryFn: async () => (await api.get("/trending/velocity", { params: { window: win, limit: 3 } })).data,
    refetchInterval: 120_000,
  });
  const items = data?.items || [];

  useEffect(() => {
    if (touched.current || !data || data.items?.length) return;
    if (win === "10m") setWin("1h");
    else if (win === "1h") setWin("24h");
  }, [data, win]);

  const winLabel = WINDOWS.find((w) => w.key === win)?.label || win;

  return (
    <div className="bg-black border border-white/[0.08] rounded-xl min-w-0 overflow-hidden lg:flex-none" data-testid="trending-now-panel">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/[0.07]">
        <p className="flex items-center gap-2 font-mono text-[10.5px] font-bold uppercase tracking-[0.18em] text-gray-200">
          <Zap className="h-3.5 w-3.5 text-[#F59E0B]" aria-hidden /> Trending Now
        </p>
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="trending-window-trigger" aria-label="Trending timeframe"
            className="flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.14em] text-gray-400 hover:text-white motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/60 rounded px-1 py-0.5">
            {winLabel}
            <ChevronDown className="h-3 w-3" aria-hidden />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#0d0f16] border-white/10 text-gray-200 min-w-[110px]">
            {WINDOWS.map((w) => (
              <DropdownMenuItem key={w.key} data-testid={`trending-window-${w.key}`}
                className="font-mono text-[10px] uppercase tracking-wider cursor-pointer"
                onClick={() => { touched.current = true; setWin(w.key); homeTrack("trending_now", `window_${w.key}`); }}>
                {w.label}{w.key === win ? " ✓" : ""}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div>
        {items.map((t: any, i: number) => <TrendingRow key={t.slug} t={t} i={i} winLabel={winLabel} />)}
        {isLoading && !items.length && [0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3 border-t border-white/[0.05] first:border-0" aria-hidden>
            <span className="h-6 w-6 rounded-full bg-white/[0.04] animate-pulse shrink-0" />
            <span className="flex-1 space-y-2">
              <span className="block h-3 w-4/5 rounded bg-white/[0.05] animate-pulse" />
              <span className="block h-2 w-24 rounded bg-white/[0.04] animate-pulse" />
            </span>
          </div>
        ))}
        {!isLoading && !isError && !items.length && (
          <p className="px-4 py-6 text-[12.5px] text-gray-500" data-testid="trending-empty">
            No accelerating stories in this window yet.
          </p>
        )}
        {isError && (
          <p className="px-4 py-6 text-[12.5px] text-gray-500" data-testid="trending-error">
            Trending signals are briefly unavailable.
          </p>
        )}
      </div>
      <p className="px-4 py-2.5 border-t border-white/[0.06] font-mono text-[8.5px] uppercase tracking-[0.14em] text-gray-600">
        Ranked by article views · last {winLabel}
      </p>
    </div>
  );
};
